// PROTO.A1 (#1619) — static, authored UI copy for the Prototype Agent
// tradeoff explanation shown at project setup (D9). Originally verbatim from
// the platform-governance ADR-002 §4 ("Client-Selectable Fidelity — The
// Most Consequential Decision"); a later platform-governance decision
// (2026-07-31, Drive-hosted, informally called "ADR-003" in earlier
// revisions of this comment — NOT this repo's docs/adr/adr-003-help-agent.md,
// and not a runtime ADR lookup) collapsed the three client-selectable tiers
// to HIGH-only and this file's copy was rewritten accordingly (v3). Shared
// between apps/web (renders it) and apps/api
// (persists tradeoffCopyVersion for provenance) so there is exactly one
// source of truth for the text, mirroring how other cross-cutting
// constants live in this package (see index.ts).
//
// Bump CURRENT_TRADEOFF_COPY_VERSION whenever this text changes.
// project_prototype_agent_config.tradeoffCopyVersion pins which version a
// project's acknowledgement covers.

export const CURRENT_TRADEOFF_COPY_VERSION = 3;

// Fidelity-collapse decision (2026-07-31, see file header) — the
// anti-anchoring lever moved from *which fidelity* to *whether and when to
// generate*: there is no fidelity choice left to warn about, so this now
// speaks to opt-in timing instead of tier selection.
export const PROTOTYPE_AGENT_GOVERNING_PRINCIPLE =
  'The quality of the prototype is directly proportional to the quality of the PRD. ' +
  'A vague requirements dialogue produces a vague PRD. A vague PRD produces a vague ' +
  'prototype. The prototype cannot compensate for requirements that were not articulated. ' +
  'Opting into prototype generation on the back of a superficial PM Agent dialogue ' +
  'will produce a convincing prototype of the wrong system. The Technology Advisor ' +
  'or PM facilitator must wait until the PRD is solid before opting in, not just present.';

export interface PrototypeFidelityCopyEntry {
  // Fidelity-collapse decision (see file header) — narrowed from
  // 'LOW' | 'MEDIUM' | 'HIGH': this array now
  // always holds exactly the one entry below. (Unrelated to, and not to be
  // confused with, apps/api's InvokePrototypeAgentParams['fidelity'], which
  // stays the wider union for pre-existing config/run rows — see that
  // file's own comment.)
  level: 'HIGH';
  label: string;
  whatYouGet: string;
  bestFor: string;
  tradeoff: string;
}

// Fidelity-collapse decision (see file header) — LOW and MEDIUM entries
// removed (renderers retired/dormant, see prototypeRenderers/lowFidelity.ts
// and the now-deleted mediumFidelity.ts).
// Exactly one entry: HIGH is the Prototype Agent's only output.
export const PROTOTYPE_AGENT_FIDELITY_COPY: PrototypeFidelityCopyEntry[] = [
  {
    level: 'HIGH',
    label: 'High fidelity (Working demo)',
    whatYouGet:
      'Fully navigable HTML application with complete styling, mock data, ' +
      'role-based login, and all primary workflows interactive. This is the ' +
      'only prototype the Prototype Agent produces — there is no lower-fidelity ' +
      'option to choose instead.',
    bestFor:
      'Confirming whether the right screens exist, whether roles see the right ' +
      'things, and whether the primary workflows feel correct. The prototype is ' +
      'interactive (forms submit, a live counter updates), so this is a real test ' +
      'of the workflows, not a guess from a static mockup.',
    tradeoff:
      'Highest possible dependency on PRD quality, and there is no deliberately-' +
      'plain alternative to fall back on. A poorly specified PRD produces a ' +
      'convincing prototype of the wrong system, which can anchor the client on ' +
      'incorrect requirements. The discipline is in WHEN you opt in, not which ' +
      'fidelity you pick — wait until the PRD is solid, not just present.',
  },
];
